import { Injectable } from "@angular/core";
import { BaseService } from "./base.service";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, catchError, map, Observable, tap } from "rxjs";

export type LimitCryptoStatus =
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'CANCELED'
    | 'PENDING_DOCUMENTS'
    | 'PENDING_REVIEW';

export interface LimitStatusModalState {
    visible: boolean;
    status: LimitCryptoStatus | '';
    title: string;
    description: string;
    requiresDocuments: boolean;
}

export interface MovementCryptoQueryParams {
    start: Date | string;
    end: Date | string;
    page?: number;
    limit?: number;
    status?: string;
    coin?: string;
    type?: string;
    order?: string;
    textSearch?: string;
}

@Injectable({
    providedIn: 'root',
})
export class CryptoService extends BaseService {
    private readonly limitStatusModalSubject = new BehaviorSubject<LimitStatusModalState>({
        visible: false,
        status: '',
        title: '',
        description: '',
        requiresDocuments: false,
    });

    readonly limitStatusModal$ = this.limitStatusModalSubject.asObservable();

    private dismissedStatus: LimitCryptoStatus | '' = '';
    private lastKnownStatus: LimitCryptoStatus | '' = '';

    constructor(http: HttpClient) {
        super(http);
    }

    generatePix(payload: any): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/onlyup-bb/pix/generate-pix`,
            payload,
            this.authorizedHeader()
        ).pipe(
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }

    // conversionCrypto(payload: any): Observable<any> {
    //     return this.http.post<any>(
    //         `${this.baseUrl}/onlyup-bb/conversion-crypto`,
    //         payload,
    //         this.authorizedHeader()
    //     ).pipe(
    //         map(this.extractData),
    //         catchError((error) => this.serviceError(error)),
    //     );
    // }

    withdrawUsdt(payload: any): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/onlyup-bb/withdraw-usdt`,
            this.encrypt(payload),
            this.authorizedHeader()
        ).pipe(
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }

    // onlyupbbGetFee(): Observable<any> {
    //     return this.http.get<any>(
    //         `${this.baseUrl}/onlyup-bb/get-fee`,
    //         this.authorizedHeader()
    //     ).pipe(
    //         map(this.extractData),
    //         catchError((error) => this.serviceError(error)),
    //     );
    // }

    onlyupbbGetFee(dto: any): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/onlyup-bb/get-quote-otc`,
            dto,
            this.authorizedHeader()
        ).pipe(
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }

    getLimitAndBalanceCrypto(): Observable<any> {
        return this.http.get<any>(
            `${this.baseUrl}/onlyup-bb/get-limit-and-balance-crypto`,
            this.authorizedHeader()
        ).pipe(
            tap((response) => {
                this.processLimitStatus(response);
            }),
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }

    dismissLimitStatusModal(): void {
        const state = this.limitStatusModalSubject.value;

        if (state.status === 'CANCELED') {
            return;
        }

        this.dismissedStatus = state.status;
        this.limitStatusModalSubject.next({
            ...state,
            visible: false,
        });
    }

    setLimitStatusModalByStatus(status: LimitCryptoStatus): void {
        this.dismissedStatus = '';
        this.lastKnownStatus = status;

        const modalState = this.buildModalState(status);

        if (!modalState) {
            this.limitStatusModalSubject.next({
                visible: false,
                status: '',
                title: '',
                description: '',
                requiresDocuments: false,
            });
            return;
        }

        this.limitStatusModalSubject.next(modalState);
    }

    getMovementBbCrypto(query: MovementCryptoQueryParams): Observable<any> {
        const queryString = this.buildMovementBbCryptoQueryString(query);

        return this.http.get<any>(
            `${this.baseUrl}/onlyup-bb/get-movement-bb-crypto-query${queryString}`,
            this.authorizedHeader()
        ).pipe(
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }

    private buildMovementBbCryptoQueryString(query: MovementCryptoQueryParams): string {
        const params: Record<string, string> = {
            start: this.toIsoString(query.start),
            end: this.toIsoString(query.end),
        };

        if (query.page != null) {
            params['page'] = String(query.page);
        }
        if (query.limit != null) {
            params['limit'] = String(query.limit);
        }
        if (query.status?.trim()) {
            params['status'] = query.status.trim();
        }
        if (query.coin?.trim()) {
            params['coin'] = query.coin.trim();
        }
        if (query.type?.trim()) {
            params['type'] = query.type.trim();
        }
        if (query.order?.trim()) {
            params['order'] = query.order.trim();
        }
        if (query.textSearch?.trim()) {
            params['textSearch'] = query.textSearch.trim();
        }

        const queryString = Object.entries(params)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return queryString ? `?${queryString}` : '';
    }

    private toIsoString(value: Date | string): string {
        return value instanceof Date ? value.toISOString() : value;
    }

    private processLimitStatus(response: any): void {
        const payload = response?.data ?? response ?? {};
        const limitCrypto = payload?.limitCrypto ?? {};
        const status = String(limitCrypto?.status || '').toUpperCase() as LimitCryptoStatus | '';

        if (!status) {
            return;
        }

        this.lastKnownStatus = status;

        if (status === 'APPROVED') {
            this.dismissedStatus = '';
            this.limitStatusModalSubject.next({
                visible: false,
                status: '',
                title: '',
                description: '',
                requiresDocuments: false,
            });
            return;
        }

        if (this.dismissedStatus === status) {
            return;
        }

        const modalState = this.buildModalState(status);
        if (!modalState) {
            return;
        }

        const current = this.limitStatusModalSubject.value;
        const isSameStatusVisible = current.visible && current.status === modalState.status;

        if (isSameStatusVisible) {
            return;
        }

        this.limitStatusModalSubject.next(modalState);
    }

    private buildModalState(status: LimitCryptoStatus): LimitStatusModalState | null {
        if (status === 'PENDING') {
            return {
                visible: true,
                status,
                title: 'Limite pendente',
                description: 'Seu limite está pendente. Aguarde a conclusão da análise.',
                requiresDocuments: false,
            };
        }

        if (status === 'REJECTED') {
            return {
                visible: true,
                status,
                title: 'Limite rejeitado',
                description: 'Seu limite foi rejeitado. Reenvie os documentos para uma nova análise.',
                requiresDocuments: true,
            };
        }

        if (status === 'PENDING_DOCUMENTS') {
            return {
                visible: true,
                status,
                title: 'Documentos pendentes',
                description: 'Precisamos dos documentos para avançar com sua análise.',
                requiresDocuments: true,
            };
        }

        if (status === 'PENDING_REVIEW') {
            return {
                visible: true,
                status,
                title: 'Em análise',
                description: 'Seus documentos já foram recebidos. Aguarde a aprovação da equipe.',
                requiresDocuments: false,
            };
        }

        if (status === 'CANCELED') {
            return {
                visible: true,
                status,
                title: 'Limite cancelado',
                description: 'Seu limite foi cancelado. Entre em contato com o suporte para orientações.',
                requiresDocuments: false,
            };
        }

        return null;
    }

    sendDocuments(formData: FormData): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/backoffice/limit-crypto`,
            formData,
            this.authorizedHeaderMulti()
        ).pipe(
            map(this.extractData),
            catchError((error) => this.serviceError(error)),
        );
    }
}